/**
 * @file Fixed pill at the top of the screen showing the exam session label.
 * Includes tamper detection: if the DOM content is modified (for example via DevTools), a
 * MutationObserver forces a re-render to restore it.
 */
import moment from 'moment';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { UserStore } from '@/store';
import { datetime } from '@/utils';
import { sendHeartbeat } from './heartbeatClient';
import './style.less';
/**
 * How often to send a heartbeat to the backend. Must be less than
 * ANIMATION_CYCLE x ANIMATION_EXPIRY_CYCLES, otherwise the animation would
 * expire before the next check has a chance to renew it.
 */
const HEARTBEAT_INTERVAL = moment.duration(10, 's');
/**
 * Minimum time between heartbeats. Visibility/pageshow listeners skip an
 * immediate heartbeat if one was sent less than this long ago, so we don't
 * burn through the backend rate-limit burst on tab switches.
 */
const MIN_HEARTBEAT_GAP = moment.duration(6, 's');
/** How often to check whether the pill DOM content was tampered with. */
const TAMPER_RECHECK_INTERVAL = moment.duration(1, 'm');
/**
 * Duration of a single shimmer animation cycle. Independent of
 * HEARTBEAT_INTERVAL — the animation may be renewed mid-cycle.
 */
const ANIMATION_CYCLE = moment.duration(12, 's');
/**
 * Number of full animation cycles before the session is considered unverified.
 * Total grace period is ANIMATION_CYCLE x ANIMATION_EXPIRY_CYCLES. The
 * animation naturally expires after this many cycles; if timers are cleared by
 * a student, no renewal happens and the shimmer stops on its own.
 */
const ANIMATION_EXPIRY_CYCLES = 3;
/** Toast tag used for the session-terminated notification. */
const HEARTBEAT_TERMINATED_TOAST_TAG = 'HeartbeatTerminated';
export default tsx.component({
    name: 'exam-session-pill',
    props: {
        /** The label to display. */
        label: p(String).required,
    },
    /** @returns The initial data. */
    data() {
        return {
            observer: undefined,
            rerenderTimer: undefined,
            animation: undefined,
            destroyed: false,
            lastLoginCheck: moment.utc(),
            lastHeartbeatSentAt: moment.utc(),
            heartbeatTimer: undefined,
        };
    },
    /** Set up tamper detection after each render. */
    mounted() {
        this.renewAnimation();
        this.startObserver();
        this.rerenderTimer = setInterval(() => this.restoreIfTampered(), TAMPER_RECHECK_INTERVAL.asMilliseconds());
        this.startHeartbeatLoop();
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('pageshow', this.onPageShow);
    },
    /** Restart observer after re-render. */
    updated() {
        this.startObserver();
    },
    /** Clean up. */
    beforeDestroy() {
        this.animation?.cancel();
        this.observer?.disconnect();
        if (this.rerenderTimer != null) {
            clearInterval(this.rerenderTimer);
        }
        if (this.heartbeatTimer != null) {
            clearTimeout(this.heartbeatTimer);
        }
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        window.removeEventListener('pageshow', this.onPageShow);
        this.destroyed = true;
    },
    computed: {
        /** @returns The label, with an unverified suffix when the animation has expired. */
        fullLabel() {
            if (this.animation == null) {
                return `${this.label} (Not Verified)`;
            }
            return this.label;
        },
    },
    methods: {
        /** Start a heartbeat loop that keeps the exam session alive. */
        startHeartbeatLoop() {
            /** Send a single heartbeat and schedule the next one. */
            const beat = async () => {
                if (this.destroyed) {
                    return;
                }
                let newExpiresAt;
                try {
                    newExpiresAt = await sendHeartbeat();
                }
                catch {
                    // Capture root before logout — the component is unmounted
                    // reactively when user state clears.
                    const root = this.$root;
                    await UserStore.logout({ revoke: false });
                    root.$addToast({
                        tag: HEARTBEAT_TERMINATED_TOAST_TAG,
                        title: 'Session terminated',
                        message: 'Your exam session was terminated because the connection was interrupted.',
                        variant: 'danger',
                    });
                    return;
                }
                UserStore.updateExpiresAt(newExpiresAt);
                this.lastHeartbeatSentAt = moment.utc();
                this.lastLoginCheck = moment.utc();
                if (this.animation == null) {
                    this.renewAnimation();
                }
                this.scheduleNextHeartbeat();
            };
            void beat();
        },
        /** Schedule the next heartbeat after {@link HEARTBEAT_INTERVAL}. */
        scheduleNextHeartbeat() {
            if (this.destroyed) {
                return;
            }
            if (this.heartbeatTimer != null) {
                clearTimeout(this.heartbeatTimer);
            }
            this.heartbeatTimer = setTimeout(() => this.startHeartbeatLoop(), HEARTBEAT_INTERVAL.asMilliseconds());
        },
        /** Handle the page becoming visible (e.g. Tab switch). */
        onVisibilityChange() {
            if (document.visibilityState === 'visible') {
                this.maybeSendImmediateHeartbeat();
            }
        },
        /**
         * Detect bfcache restore after laptop wake or back-navigation.
         *
         * @param event The page transition event.
         */
        onPageShow(event) {
            if (event.persisted) {
                this.maybeSendImmediateHeartbeat();
            }
        },
        /**
         * Send an immediate heartbeat if enough time has passed since the last
         * one. Skips if the gap is shorter than {@link MIN_HEARTBEAT_GAP} to
         * avoid hitting the backend rate limit.
         */
        maybeSendImmediateHeartbeat() {
            if (this.destroyed) {
                return;
            }
            const gap = datetime.diff(this.lastHeartbeatSentAt, moment.utc()).asMilliseconds();
            if (gap < MIN_HEARTBEAT_GAP.asMilliseconds()) {
                return;
            }
            if (this.heartbeatTimer != null) {
                clearTimeout(this.heartbeatTimer);
                this.heartbeatTimer = undefined;
            }
            this.startHeartbeatLoop();
        },
        /** Restore the pill content if it was tampered with. */
        restoreIfTampered() {
            const el = this.$el;
            if (el instanceof HTMLElement && el.textContent !== this.fullLabel) {
                el.textContent = this.fullLabel;
            }
        },
        /**
         * Start watching for DOM tampering.
         *
         * Note: we recreate the `MutationObserver` on every mutation so that even if Vue
         * creates a new element when restoring a tampered-with element we will observe it.
         */
        startObserver() {
            this.observer?.disconnect();
            this.observer = new MutationObserver(() => {
                this.observer?.disconnect();
                this.restoreIfTampered();
                this.startObserver();
            });
            this.observer.observe(this.$el, {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true,
            });
        },
        /** @returns Whether the last login check is older than the allowed grace period. */
        getVerificationIsExpired() {
            const maxAge = ANIMATION_CYCLE.asMilliseconds() * ANIMATION_EXPIRY_CYCLES;
            const timeSinceLastCheck = datetime
                .diff(this.lastLoginCheck, moment.utc())
                .asMilliseconds();
            return timeSinceLastCheck > maxAge;
        },
        /**
         * Start a finite shimmer animation that expires after
         * ANIMATION_EXPIRY_CYCLES cycles. Each successful heartbeat renews it.
         * If timers are cleared by a student, no renewal happens and the
         * animation naturally stops — making the absence of the shimmer a
         * visible signal that the session is no longer verified.
         */
        renewAnimation() {
            this.animation?.cancel();
            if (this.getVerificationIsExpired()) {
                this.animation = undefined;
                return;
            }
            const el = this.$el;
            if (!(el instanceof HTMLElement)) {
                return;
            }
            // Cancel any CSS animations so clones won't match our Web Animation
            const animations = el.getAnimations();
            animations.forEach(anim => anim.cancel());
            this.animation = el.animate([
                { backgroundPosition: '100% 0' },
                { backgroundPosition: '0 100%', offset: 0.5 },
                { backgroundPosition: '100% 0' },
            ], {
                duration: ANIMATION_CYCLE.asMilliseconds(),
                iterations: ANIMATION_EXPIRY_CYCLES,
                easing: 'linear',
            });
            /** Either clean up or renew the animation when a cycle finishes. */
            this.animation.onfinish = () => {
                if (this.destroyed || this.getVerificationIsExpired()) {
                    this.animation?.cancel();
                    this.animation = undefined;
                }
                else {
                    this.renewAnimation();
                }
            };
        },
    },
    /** @returns The rendered pill. */
    render() {
        return (<div class={[
                'cg-exam-pill label-medium',
                { 'cg-exam-pill--active': this.animation != null },
            ]}>
                {this.fullLabel}
            </div>);
    },
});
